import { useMemo, useCallback, useRef, useState } from 'react';
import { Database } from 'lucide-react';
import { useWorkspaceStore, getAllPanes } from '@/stores/workspace-store';
import { TabBar } from './TabBar';
import { TableView } from './TableView';
import { InfoView } from './InfoView';
import { QueryEditor } from './QueryEditor';
import { TunnelView } from './TunnelView';
import { StructureView } from './StructureView';
import { TerminalView } from './TerminalView';
import { AdminShellView } from './AdminShellView';
import { TableDesigner } from './TableDesigner';
import { CodeEditorView } from './CodeEditorView';
import { ERDView } from './ERDView';
import { DiffView } from './DiffView';
import { MarkdownView } from './MarkdownView';
import { ResizeHandle } from '@/components/layout/ResizeHandle';
import type { PaneOrSplit, PaneState } from '@/types';

function SplitRenderer({ node }: { node: PaneOrSplit }) {
  if (node.type === 'pane') {
    return <PaneRenderer pane={node.pane} />;
  }

  const isHorizontal = node.direction === 'horizontal';
  const setSplitRatio = useWorkspaceStore((s) => s.setSplitRatio);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use a ref to always read the latest ratio — avoids stale closure during drag
  const ratioRef = useRef(node.ratio);
  ratioRef.current = node.ratio;

  const handleResize = useCallback(
    (delta: number) => {
      const container = containerRef.current;
      if (!container) return;
      const totalSize = isHorizontal ? container.offsetWidth : container.offsetHeight;
      if (totalSize === 0) return;
      const ratioDelta = delta / totalSize;
      setSplitRatio(node.id, ratioRef.current + ratioDelta);
    },
    [node.id, isHorizontal, setSplitRatio],
  );

  return (
    <div
      ref={containerRef}
      className={`flex ${isHorizontal ? 'flex-row' : 'flex-col'} flex-1 min-w-0 min-h-0`}
    >
      <div
        style={
          isHorizontal
            ? { width: `${node.ratio * 100}%`, minWidth: 0 }
            : { height: `${node.ratio * 100}%`, minHeight: 0 }
        }
        className="flex flex-col min-w-0 min-h-0"
      >
        <SplitRenderer node={node.children[0]} />
      </div>
      <ResizeHandle
        direction={isHorizontal ? 'vertical' : 'horizontal'}
        onResize={handleResize}
      />
      <div
        style={{ flex: 1 }}
        className="min-w-0 min-h-0 flex flex-col"
      >
        <SplitRenderer node={node.children[1]} />
      </div>
    </div>
  );
}

function PaneRenderer({ pane }: { pane: PaneState }) {
  const focusedPaneId = useWorkspaceStore((s) => s.focusedPaneId);
  const setFocusedPane = useWorkspaceStore((s) => s.setFocusedPane);
  const moveTab = useWorkspaceStore((s) => s.moveTab);
  const isFocused = pane.id === focusedPaneId;
  const [dragOver, setDragOver] = useState(false);

  const activeTab = useMemo(
    () => pane.tabs.find((t) => t.id === pane.activeTabId) ?? null,
    [pane.tabs, pane.activeTabId],
  );

  const handleFocus = useCallback(() => {
    if (!isFocused) {
      setFocusedPane(pane.id);
    }
  }, [isFocused, pane.id, setFocusedPane]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain')) as {
        tabId: string;
        paneId: string;
      };
      if (data.paneId !== pane.id) {
        moveTab(data.tabId, data.paneId, pane.id);
      }
    } catch {
      // Ignore invalid drag data
    }
  }, [pane.id, moveTab]);

  if (pane.tabs.length === 0) {
    return (
      <div
        className={`flex-1 flex flex-col items-center justify-center bg-dbgo-workarea text-text-muted ${
          dragOver ? 'ring-2 ring-inset ring-accent-blue/50' : ''
        }`}
        onMouseDown={handleFocus}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Database size={32} className="mb-3 opacity-30" />
        <p className="text-sm">{dragOver ? 'Drop tab here' : 'No tabs open'}</p>
        <p className="text-xs mt-1 opacity-60">
          {dragOver ? '' : 'Select a table or connection from the sidebar'}
        </p>
      </div>
    );
  }

  return (
    <div
      className={`flex-1 flex flex-col min-w-0 min-h-0 bg-dbgo-workarea ${
        isFocused ? '' : 'opacity-[0.97]'
      }`}
      onMouseDown={handleFocus}
    >
      <TabBar paneId={pane.id} tabs={pane.tabs} activeTabId={pane.activeTabId} />
      {/*
        Key the inner container by `activeTab.id` so React remounts the
        per-tab component on every tab switch. Without this, tab views
        like TableView hold their own `useState(tab.where)` state that
        only initializes on first mount — the next tab inherits the
        previous tab's WHERE / ORDER BY / pending edits / column config
        because React reuses the same component instance with a new
        prop. Each tab is its own "view"; per-tab state should not leak.
        TanStack Query cache survives unmount so re-render doesn't
        re-fetch; xterm + tmux survives via sessionId resume; nothing
        else stays around long enough to matter.
      */}
      {activeTab ? (
        <div key={activeTab.id} className="flex-1 min-h-0 relative">
          {activeTab.type === 'table' && <TableView tab={activeTab} />}
          {activeTab.type === 'info' && <InfoView tab={activeTab} />}
          {activeTab.type === 'query' && <QueryEditor tab={activeTab} />}
          {activeTab.type === 'tunnel' && <TunnelView tab={activeTab} />}
          {activeTab.type === 'structure' && <StructureView tab={activeTab} />}
          {activeTab.type === 'terminal' && <TerminalView tab={activeTab} />}
          {activeTab.type === 'designer' && <TableDesigner tab={activeTab} />}
          {activeTab.type === 'code-editor' && <CodeEditorView tab={activeTab} />}
          {activeTab.type === 'erd' && <ERDView tab={activeTab} />}
          {activeTab.type === 'diff' && <DiffView tab={activeTab} />}
          {activeTab.type === 'markdown' && <MarkdownView tab={activeTab} />}
          {activeTab.type === 'admin-shell' && <AdminShellView />}
        </div>
      ) : (
        <div className="flex-1 min-h-0 relative flex items-center justify-center text-xs text-text-muted">
          Select a tab
        </div>
      )}
    </div>
  );
}

export function Workarea() {
  const root = useWorkspaceStore((s) => s.root);

  // Check if there are any tabs at all across all panes
  const allPanes = getAllPanes(root);
  const hasTabs = allPanes.some((p) => p.tabs.length > 0);

  if (!hasTabs && allPanes.length === 1) {
    // Single empty pane — show welcome
    return <PaneRenderer pane={allPanes[0]} />;
  }

  return (
    <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-dbgo-workarea">
      <SplitRenderer node={root} />
    </div>
  );
}
